// -----------------------------------------------------------
// FloraIQ - Fotos de exemplar ja cadastrado (coletor e FCA)
// -----------------------------------------------------------
// Alem da foto principal, toda foto do exemplar e evidencia de uma vistoria:
// vai para a vistoria aberta do exemplar ou abre uma de monitoramento, que
// fica em rascunho ate alguem completar e enviar.

import { inspectionApi, type ArborInspectionSummaryApi } from "./api";
import { computeFileSha256 } from "./fileChecksum";
import { PHOTO_CATEGORIES } from "@/config/photo-categories";

/** Vistorias em que ainda se anexa evidencia (mesma regra do servidor). */
export const OPEN_INSPECTION_STATUSES = ["DRAFT", "IN_PROGRESS", "RETURNED"];
export const MAX_PHOTO_BYTES = 160 * 1024 * 1024;
// Acima disso o hash no navegador fica pesado; o servidor calcula de qualquer forma.
export const MAX_CLIENT_HASH_BYTES = 20 * 1024 * 1024;

export const PHOTO_TYPES = PHOTO_CATEGORIES.map((c) => ({ value: c.code, label: c.label, group: c.group }));

type InspectionLike = Pick<ArborInspectionSummaryApi, "id" | "workflow_status" | "created_at" | "inspection_date">;

/** Vistoria aberta mais recente do exemplar, ou nenhuma. */
export function pickOpenInspection<T extends InspectionLike>(inspections: T[]): T | undefined {
  return inspections
    .filter((i) => OPEN_INSPECTION_STATUSES.includes(i.workflow_status))
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
}

export async function clientChecksum(file: File): Promise<string | null> {
  return file.size <= MAX_CLIENT_HASH_BYTES ? computeFileSha256(file) : null;
}

export interface UploadPhotosResult {
  sent: number;
  inspectionId: number;
  createdInspection: boolean;
}

/**
 * Envia as fotos como evidencias da vistoria aberta do exemplar, abrindo uma
 * vistoria de monitoramento quando nao houver. Para no primeiro erro: as fotos
 * ja enviadas continuam gravadas e `sent` diz quantas.
 */
export async function uploadTreePhotos(
  treeId: number,
  files: File[],
  opts: {
    evidenceType: string;
    latitude?: number | null;
    longitude?: number | null;
    source: string;
    onProgress?: (text: string) => void;
  },
): Promise<UploadPhotosResult> {
  const images = files.filter((f) => f.type.startsWith("image/") && f.size <= MAX_PHOTO_BYTES);
  if (images.length === 0) throw new Error("Selecione fotos (imagem de até 160 MB).");

  const open = pickOpenInspection(await inspectionApi.listByTree(treeId));
  let inspectionId = open?.id;
  let createdInspection = false;
  if (!inspectionId) {
    opts.onProgress?.("Abrindo vistoria de monitoramento...");
    const created = await inspectionApi.create(treeId, {
      inspection_type: "MONITORING",
      inspection_date: new Date().toISOString().slice(0, 10),
      notes: `Registro fotográfico pelo ${opts.source}.`,
    });
    inspectionId = created.id;
    createdInspection = true;
  }

  let sent = 0;
  for (const file of images) {
    opts.onProgress?.(`Enviando foto ${sent + 1} de ${images.length}...`);
    await inspectionApi.uploadEvidence(inspectionId, file, {
      evidenceType: opts.evidenceType,
      latitude: opts.latitude ?? undefined,
      longitude: opts.longitude ?? undefined,
      clientChecksumSha256: await clientChecksum(file),
    });
    sent += 1;
  }
  return { sent, inspectionId, createdInspection };
}
